import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import type {
  BookingStatusResponse,
  BookingStep,
  CreditPassSummary,
  StatusView,
  YogaBookingControls,
} from '../types';
import {
  getReturnOrigin,
  getReturnPath,
  readReturnTokenFromHash,
  resolveStatusView,
  stripReturnTokenFromHash,
} from '../lib/returnHash';
import { Button } from './Button';
import { Modal } from './Modal';
import { TriggerButton } from './TriggerButton';
import { EntryStep } from './steps/EntryStep';
import { SessionStep } from './steps/SessionStep';
import { ProductStep } from './steps/ProductStep';
import { CustomerStep, type CustomerFormValues } from './steps/CustomerStep';
import { StatusStep } from './steps/StatusStep';
import { RedeemEmailStep } from './steps/RedeemEmailStep';
import { RedeemCodeStep } from './steps/RedeemCodeStep';
import { RedeemPassStep } from './steps/RedeemPassStep';
import { Spinner } from './Spinner';
import styles from './BookingWidget.module.css';

const CUSTOMER_TOKEN_KEY = 'yoga-customer-access-token';

const PURCHASE_STEPS: BookingStep[] = ['session', 'product', 'customer', 'status'];
const REDEEM_STEPS: BookingStep[] = [
  'redeemEmail',
  'redeemCode',
  'redeemPass',
  'redeemSession',
  'status',
];

const STEP_TITLES: Record<BookingStep, { title: string; subtitle: string }> = {
  entry: {
    title: 'Buchung',
    subtitle: 'Wie möchtest du fortfahren?',
  },
  session: {
    title: 'Termin wählen',
    subtitle: 'Wähle deinen Wunschtermin',
  },
  product: {
    title: 'Angebot wählen',
    subtitle: 'Einzelstunde, Paket oder Schnupper-Angebot',
  },
  customer: {
    title: 'Deine Daten',
    subtitle: 'Fast geschafft – nur noch deine Kontaktdaten',
  },
  status: {
    title: 'Buchungsstatus',
    subtitle: 'Hier siehst du den aktuellen Stand',
  },
  redeemEmail: {
    title: 'Guthaben einlösen',
    subtitle: 'Wir senden dir einen Code an deine E-Mail',
  },
  redeemCode: {
    title: 'Code eingeben',
    subtitle: 'Prüfe dein Postfach und gib den 6-stelligen Code ein',
  },
  redeemPass: {
    title: 'Pass wählen',
    subtitle: 'Wähle das Guthaben für diese Buchung',
  },
  redeemSession: {
    title: 'Termin wählen',
    subtitle: 'Wähle den Termin für dein Guthaben',
  },
};

interface BookingWidgetProps {
  studioSlug: string;
  apiUrl: string;
  showTrigger?: boolean;
  onReady?: (controls: YogaBookingControls) => void;
}

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

function readStoredCustomerToken(): string | null {
  try {
    return sessionStorage.getItem(CUSTOMER_TOKEN_KEY);
  } catch {
    return null;
  }
}

function storeCustomerToken(token: string) {
  try {
    sessionStorage.setItem(CUSTOMER_TOKEN_KEY, token);
  } catch {
    // ignore quota / private mode
  }
}

function clearCustomerToken() {
  try {
    sessionStorage.removeItem(CUSTOMER_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function BookingWidget({
  studioSlug,
  apiUrl,
  showTrigger = true,
  onReady,
}: BookingWidgetProps) {
  const [queryClient] = useState(createQueryClient);
  const api = useMemo(
    () => createApiClient(apiUrl, studioSlug),
    [apiUrl, studioSlug],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <BookingWidgetInner api={api} showTrigger={showTrigger} onReady={onReady} />
    </QueryClientProvider>
  );
}

function BookingWidgetInner({
  api,
  showTrigger,
  onReady,
}: {
  api: ReturnType<typeof createApiClient>;
  showTrigger: boolean;
  onReady?: (controls: YogaBookingControls) => void;
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<BookingStep>('entry');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [statusView, setStatusView] = useState<StatusView | null>(null);
  const [statusData, setStatusData] = useState<BookingStatusResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [returnLoading, setReturnLoading] = useState(false);

  const [redeemEmail, setRedeemEmail] = useState('');
  const [customerAccessToken, setCustomerAccessToken] = useState<string | null>(null);
  const [creditPasses, setCreditPasses] = useState<CreditPassSummary[]>([]);
  const [creditPassId, setCreditPassId] = useState<string | null>(null);
  const [passesLoading, setPassesLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const resetFlow = useCallback(() => {
    setStep('entry');
    setSessionId(null);
    setProductId(null);
    setBookingId(null);
    setAccessToken(null);
    setStatusView(null);
    setStatusData(null);
    setServerError(null);
    setInfoMessage(null);
    setIsSubmitting(false);
    setRedeemEmail('');
    setCreditPasses([]);
    setCreditPassId(null);
    setPassesLoading(false);
    setIsResending(false);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (step === 'status') {
      resetFlow();
    }
  }, [resetFlow, step]);

  useEffect(() => {
    onReady?.({
      open: () => setOpen(true),
      close: handleClose,
    });
  }, [onReady, handleClose]);

  const loadPassesAndContinue = useCallback(
    async (token: string) => {
      setPassesLoading(true);
      setServerError(null);
      try {
        const passes = await api.getCreditPasses(token);
        setCreditPasses(passes);
        setCustomerAccessToken(token);
        storeCustomerToken(token);

        if (passes.length === 0) {
          setCreditPassId(null);
          setStep('redeemPass');
          return;
        }

        if (passes.length === 1) {
          setCreditPassId(passes[0]!.id);
          setStep('redeemSession');
          return;
        }

        setCreditPassId(null);
        setStep('redeemPass');
      } catch (error) {
        clearCustomerToken();
        setCustomerAccessToken(null);
        setServerError(
          error instanceof Error
            ? error.message
            : 'Guthaben konnte nicht geladen werden.',
        );
        setStep('redeemEmail');
      } finally {
        setPassesLoading(false);
      }
    },
    [api],
  );

  const handleReturnToken = useCallback(
    async (token: string) => {
      setReturnLoading(true);
      setOpen(true);
      setStep('status');
      setServerError(null);

      try {
        const result = await api.getWidgetReturn(token);
        setBookingId(result.bookingId);
        setAccessToken(result.accessToken);
        setStatusData(result);
        setStatusView(resolveStatusView(result));
      } catch (error) {
        setServerError(
          error instanceof Error
            ? error.message
            : 'Rückkehr konnte nicht verarbeitet werden.',
        );
        setStep('status');
        setStatusView('failed');
      } finally {
        setReturnLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    const token = readReturnTokenFromHash();
    if (!token) {
      return;
    }

    stripReturnTokenFromHash();
    void handleReturnToken(token);
  }, [handleReturnToken]);

  const copy = STEP_TITLES[step];
  const purchaseStepIndex = PURCHASE_STEPS.indexOf(step);
  const redeemStepIndex = REDEEM_STEPS.indexOf(step);
  const showPurchaseDots = purchaseStepIndex >= 0 && step !== 'status';
  const showRedeemDots = redeemStepIndex >= 0 && step !== 'status';

  const goBack = () => {
    setServerError(null);
    setInfoMessage(null);
    if (step === 'session') {
      setStep('entry');
    } else if (step === 'product') {
      setStep('session');
    } else if (step === 'customer') {
      setStep('product');
    } else if (step === 'redeemEmail') {
      setStep('entry');
    } else if (step === 'redeemCode') {
      setStep('redeemEmail');
    } else if (step === 'redeemPass') {
      setStep('redeemCode');
    } else if (step === 'redeemSession') {
      if (creditPasses.length > 1) {
        setStep('redeemPass');
      } else {
        setStep('redeemCode');
      }
    }
  };

  const handleBookingSubmit = async (values: CustomerFormValues) => {
    if (!sessionId || !productId) {
      return;
    }

    setIsSubmitting(true);
    setServerError(null);

    try {
      const booking = await api.createBooking({
        classSessionId: sessionId,
        productId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        street: values.street,
        streetNumber: values.streetNumber,
        postalCode: values.postalCode,
        city: values.city,
        country: values.country,
        returnOrigin: getReturnOrigin(),
        returnPath: getReturnPath(),
      });

      setBookingId(booking.bookingId);
      setAccessToken(booking.accessToken);

      if (!booking.requiresPayment) {
        const status = await api.getBookingStatus(
          booking.bookingId,
          booking.accessToken,
        );
        setStatusData(status);
        setStatusView('confirmed');
        setStep('status');
        return;
      }

      const payment = await api.createPayment(
        booking.bookingId,
        booking.accessToken,
      );

      const checkoutUrl = payment.checkoutUrl ?? booking.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Keine Checkout-URL erhalten.');
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      setServerError(
        error instanceof Error
          ? error.message
          : 'Buchung konnte nicht erstellt werden.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestCode = async (email: string) => {
    setIsSubmitting(true);
    setServerError(null);
    setInfoMessage(null);
    try {
      const result = await api.requestAccessCode({ email });
      setRedeemEmail(email);
      setInfoMessage(result.message);
      setStep('redeemCode');
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'Code konnte nicht gesendet werden.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!redeemEmail) return;
    setIsResending(true);
    setServerError(null);
    try {
      const result = await api.requestAccessCode({ email: redeemEmail });
      setInfoMessage(result.message);
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'Code konnte nicht gesendet werden.',
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    setIsSubmitting(true);
    setServerError(null);
    setInfoMessage(null);
    try {
      const result = await api.verifyAccessCode({ email: redeemEmail, code });
      await loadPassesAndContinue(result.accessToken);
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'Code konnte nicht geprüft werden.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const startRedeem = async () => {
    setServerError(null);
    setInfoMessage(null);
    const stored = readStoredCustomerToken();
    if (stored) {
      setStep('redeemPass');
      await loadPassesAndContinue(stored);
      return;
    }
    setStep('redeemEmail');
  };

  const handleRedeemBooking = async () => {
    if (!sessionId || !creditPassId || !customerAccessToken) {
      return;
    }

    setIsSubmitting(true);
    setServerError(null);
    try {
      const booking = await api.redeemCreditBooking(customerAccessToken, {
        classSessionId: sessionId,
        creditPassId,
        returnOrigin: getReturnOrigin(),
        returnPath: getReturnPath(),
      });

      setBookingId(booking.bookingId);
      setAccessToken(booking.accessToken);
      const status = await api.getBookingStatus(booking.bookingId, booking.accessToken);
      setStatusData(status);
      setStatusView('confirmed');
      setStep('status');
    } catch (error) {
      setServerError(
        error instanceof Error
          ? error.message
          : 'Termin konnte nicht mit Guthaben gebucht werden.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showBack = step !== 'entry' && step !== 'status';

  return (
    <div className={`yoga-booking-widget ${styles.widget}`}>
      {showTrigger ? <TriggerButton onClick={() => setOpen(true)} /> : null}

      <Modal open={open} onClose={handleClose} labelledBy="yoga-booking-title">
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <h2 id="yoga-booking-title" className={styles.title}>
                {copy.title}
              </h2>
              <p className={styles.subtitle}>{copy.subtitle}</p>
            </div>
            <button
              type="button"
              className={styles.close}
              aria-label="Schliessen"
              onClick={handleClose}
            >
              ×
            </button>
          </div>

          {showPurchaseDots ? (
            <div className={styles.steps} aria-hidden="true">
              {PURCHASE_STEPS.slice(0, 3).map((item, index) => (
                <span
                  key={item}
                  className={
                    index < purchaseStepIndex
                      ? styles.stepDotDone
                      : index === purchaseStepIndex
                        ? styles.stepDotActive
                        : styles.stepDot
                  }
                />
              ))}
            </div>
          ) : null}

          {showRedeemDots ? (
            <div className={styles.steps} aria-hidden="true">
              {REDEEM_STEPS.slice(0, 4).map((item, index) => (
                <span
                  key={item}
                  className={
                    index < redeemStepIndex
                      ? styles.stepDotDone
                      : index === redeemStepIndex
                        ? styles.stepDotActive
                        : styles.stepDot
                  }
                />
              ))}
            </div>
          ) : null}
        </header>

        <div className={styles.body}>
          {returnLoading || (step === 'redeemPass' && passesLoading) ? (
            <Spinner centered large />
          ) : null}

          {!returnLoading && step === 'entry' ? (
            <EntryStep
              onPurchase={() => {
                setServerError(null);
                setStep('session');
              }}
              onRedeem={() => {
                void startRedeem();
              }}
            />
          ) : null}

          {!returnLoading && step === 'session' ? (
            <SessionStep
              api={api}
              selectedId={sessionId}
              onSelect={(session) => {
                setSessionId(session?.id ?? null);
                setServerError(null);
              }}
            />
          ) : null}

          {!returnLoading && step === 'product' ? (
            <ProductStep
              api={api}
              selectedId={productId}
              onSelect={(product) => {
                setProductId(product.id);
                setServerError(null);
              }}
            />
          ) : null}

          {!returnLoading && step === 'customer' ? (
            <CustomerStep
              onSubmit={handleBookingSubmit}
              isSubmitting={isSubmitting}
              serverError={serverError}
            />
          ) : null}

          {!returnLoading && step === 'redeemEmail' ? (
            <RedeemEmailStep
              onSubmit={handleRequestCode}
              isSubmitting={isSubmitting}
              serverError={serverError}
              infoMessage={infoMessage}
            />
          ) : null}

          {!returnLoading && step === 'redeemCode' ? (
            <RedeemCodeStep
              email={redeemEmail}
              onSubmit={handleVerifyCode}
              onResend={() => {
                void handleResendCode();
              }}
              isSubmitting={isSubmitting}
              isResending={isResending}
              serverError={serverError}
              infoMessage={infoMessage}
            />
          ) : null}

          {!returnLoading && step === 'redeemPass' && !passesLoading ? (
            <RedeemPassStep
              passes={creditPasses}
              selectedId={creditPassId}
              isLoading={false}
              error={serverError}
              onSelect={(pass) => {
                setCreditPassId(pass.id);
                setServerError(null);
              }}
            />
          ) : null}

          {!returnLoading && step === 'redeemSession' ? (
            <>
              {serverError ? <p className={styles.error}>{serverError}</p> : null}
              <SessionStep
                api={api}
                selectedId={sessionId}
                onSelect={(session) => {
                  setSessionId(session?.id ?? null);
                  setServerError(null);
                }}
              />
            </>
          ) : null}

          {!returnLoading && step === 'status' && bookingId && accessToken && statusView ? (
            <StatusStep
              api={api}
              bookingId={bookingId}
              accessToken={accessToken}
              initialView={statusView}
              initialData={statusData}
            />
          ) : null}

          {!returnLoading && step === 'status' && serverError && !bookingId ? (
            <p className={styles.error}>{serverError}</p>
          ) : null}
        </div>

        {!returnLoading && step !== 'status' && step !== 'entry' ? (
          <footer
            className={
              step === 'customer' ||
              step === 'redeemEmail' ||
              step === 'redeemCode' ||
              step === 'redeemSession'
                ? styles.footer
                : step === 'session' || step === 'product' || step === 'redeemPass'
                  ? styles.footer
                  : styles.footerSingle
            }
          >
            {showBack ? (
              <Button variant="secondary" onClick={goBack} disabled={isSubmitting}>
                Zurück
              </Button>
            ) : null}

            {step === 'session' ? (
              <Button
                fullWidth
                disabled={!sessionId}
                onClick={() => setStep('product')}
              >
                {sessionId ? 'Weiter' : 'Kurs wählen'}
              </Button>
            ) : null}

            {step === 'product' ? (
              <Button
                fullWidth
                disabled={!productId}
                onClick={() => setStep('customer')}
              >
                Weiter
              </Button>
            ) : null}

            {step === 'customer' ? (
              <Button
                fullWidth
                disabled={isSubmitting}
                onClick={() => {
                  (
                    document.getElementById('yoga-customer-form') as HTMLFormElement | null
                  )?.requestSubmit();
                }}
              >
                {isSubmitting ? (
                  <span className={styles.loadingInline}>
                    <Spinner /> Wird gebucht…
                  </span>
                ) : (
                  'Jetzt buchen'
                )}
              </Button>
            ) : null}

            {step === 'redeemEmail' ? (
              <Button
                fullWidth
                disabled={isSubmitting}
                onClick={() => {
                  (
                    document.getElementById(
                      'yoga-redeem-email-form',
                    ) as HTMLFormElement | null
                  )?.requestSubmit();
                }}
              >
                {isSubmitting ? (
                  <span className={styles.loadingInline}>
                    <Spinner /> Wird gesendet…
                  </span>
                ) : (
                  'Code senden'
                )}
              </Button>
            ) : null}

            {step === 'redeemCode' ? (
              <Button
                fullWidth
                disabled={isSubmitting}
                onClick={() => {
                  (
                    document.getElementById(
                      'yoga-redeem-code-form',
                    ) as HTMLFormElement | null
                  )?.requestSubmit();
                }}
              >
                {isSubmitting ? (
                  <span className={styles.loadingInline}>
                    <Spinner /> Wird geprüft…
                  </span>
                ) : (
                  'Weiter'
                )}
              </Button>
            ) : null}

            {step === 'redeemPass' ? (
              <Button
                fullWidth
                disabled={!creditPassId || creditPasses.length === 0}
                onClick={() => setStep('redeemSession')}
              >
                Weiter
              </Button>
            ) : null}

            {step === 'redeemSession' ? (
              <Button
                fullWidth
                disabled={!sessionId || isSubmitting}
                onClick={() => {
                  void handleRedeemBooking();
                }}
              >
                {isSubmitting ? (
                  <span className={styles.loadingInline}>
                    <Spinner /> Wird gebucht…
                  </span>
                ) : sessionId ? (
                  'Mit Guthaben buchen'
                ) : (
                  'Kurs wählen'
                )}
              </Button>
            ) : null}
          </footer>
        ) : null}

        {!returnLoading && step === 'status' ? (
          <footer className={styles.footerSingle}>
            <Button
              fullWidth
              onClick={() => {
                resetFlow();
                setOpen(false);
              }}
            >
              Schliessen
            </Button>
          </footer>
        ) : null}
      </Modal>
    </div>
  );
}
